import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"

import {
  getShareLinkFromShareID,
  useGetProjectShareLink
} from "entities/projects/model/useGetProjectShareLink"
import {
  ShareLinkInviteEmailFormData,
  useSendShareLinkInvite
} from "features/project-sharing/model/useSendShareLinkInvite"

import { ShareProjectOverlayConnectorProps } from "../ShareProjectOverlayConnector"

export type ShareProjectOverlayModel = ReturnType<
  typeof useShareProjectOverlayModel
>

export const useShareProjectOverlayModel = ({
  payload: { projectID, isShareID }
}: ShareProjectOverlayConnectorProps) => {
  const { closeOverlay } = useCurrentOverlay()
  const [shareLink, setShareLink] = useState<string | null>("")
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()
  const { createShareLink } = useGetProjectShareLink(projectID)

  const sendEmail = useSendShareLinkInvite()

  const handleShare = useEvent((formData: ShareLinkInviteEmailFormData) => {
    return sendEmail(formData)
  })

  useEffect(() => {
    if (isShareID) {
      setShareLink(getShareLinkFromShareID(projectID))
    } else {
      setIsLoading(true)
      createShareLink().then(link => {
        setIsLoading(false)
        setShareLink(link)
      })
    }
  }, [])

  const handleCopy = useEvent(() => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink).then(() => {
        raiseToast({
          message: t("General.CopiedToClipboard"),
          type: ToastType.SUCCESS
        })
      })
    }
  })

  const defaultFormData = useMemo<ShareLinkInviteEmailFormData>(
    () => ({
      email: "",
      projectID
    }),
    [projectID]
  )

  return {
    shareLink,
    isLoading,
    defaultFormData,
    onCopy: handleCopy,
    onSubmit: handleShare,
    onClose: closeOverlay
  }
}
